import { doc, getDoc } from 'firebase/firestore';
import {
  featureFlagPaths,
  resolveFeatureFlag,
  type FeatureFlagContext,
  type FeatureFlagKey,
  type FeatureFlagValue,
  type GlobalFeatureFlags,
} from '@todesktop/shared';
import { getSnapshot } from './common';
import { firestore } from './firestore';

const globalFeatureFlagsDoc = () => doc(firestore, featureFlagPaths.global);

export const onGlobalFeatureFlagsUpdate = (
  handler: (flags: GlobalFeatureFlags) => void,
) =>
  getSnapshot<GlobalFeatureFlags | undefined>(
    globalFeatureFlagsDoc(),
    (flags) => handler(flags ?? {}),
    // Flags fall back to their defaults until the global values load, and
    // for good if they can't, so pages waiting on them don't wait forever.
    (err) => {
      console.error('Failed to subscribe to feature flags', err);
      handler({});
    },
  );

/**
 * Resolves a flag with a one-off read of the global values, for code that
 * can run before the store has them, such as sign-up. Use `useFeatureFlag`
 * or `getFeatureFlag` everywhere else.
 */
export const fetchFeatureFlag = async <K extends FeatureFlagKey>(
  key: K,
  context: Omit<FeatureFlagContext, 'global'>,
): Promise<FeatureFlagValue<K>> => {
  let global: unknown;
  try {
    global = (await getDoc(globalFeatureFlagsDoc())).data();
  } catch (err) {
    console.error('Failed to read feature flags', err);
  }
  return resolveFeatureFlag(key, { ...context, global });
};
